import * as React from 'react';

import gql from 'graphql-tag';
import { compact } from 'lodash';
import { branch, compose } from 'recompose';

import useRouter from 'js/lib/useRouter';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import withNotFound from 'bundles/common/components/withNotFound';
import InstructorProfileNotFound from 'bundles/partners-graphql/components/InstructorProfileNotFound';
import InstructorProfilePage, {
  getInstructorBreadcrumbLinks,
} from 'bundles/partners-graphql/components/InstructorProfilePage';
import type {
  InstructorProfileViewerByIdQueryVariables as ByIdVariables,
  InstructorProfileViewerByIdQuery as InstructorProfileViewerByIdQueryData,
} from 'bundles/partners-graphql/components/__generated__/InstructorProfileViewerByIdQuery';
import type {
  InstructorProfileViewerByShortNameQueryVariables as ByShortNameVariables,
  InstructorProfileViewerByShortNameQuery as InstructorProfileViewerByShortNameQueryData,
} from 'bundles/partners-graphql/components/__generated__/InstructorProfileViewerByShortNameQuery';
import InstructorProfileGoogleSchemaMarkup from 'bundles/partners-graphql/components/seo/InstructorProfileGoogleSchemaMarkup';
import { BLOCKED_RUSSIAN_PARTNERS_SHORTNAMES } from 'bundles/partners-graphql/constants';
import type { Instructor } from 'bundles/partners-graphql/types/Instructor';
import { generateHref } from 'bundles/partners-graphql/utils/partnersGraphqlUtils';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';
import { getSubdomainDetails } from 'bundles/seo/utils/domainAndMetadataUtils';

import _t from 'i18n!nls/partners-graphql';

type PropsFromRouter = {
  params: {
    instructorSlug: string;
  };
};

type PropsToComponent = {
  instructor?: Instructor | null;
};

const isAffiliatedToBlockedRussianPartner = (instructor: Instructor) =>
  instructor.partners?.elements.find((partner) =>
    BLOCKED_RUSSIAN_PARTNERS_SHORTNAMES.includes(partner?.shortName || '')
  );

export const InstructorProfileViewer = ({ instructor }: PropsToComponent) => {
  const { location } = useRouter();
  const { pathname } = location;

  if (!instructor) {
    return null;
  }

  const { fullName: instructorName, partners, bio, courses } = instructor;
  const coursesList = compact(courses?.elements);

  const constructDescriptionMetaTagValue = () => {
    const partnerName = compact(partners?.elements)[0]?.name;
    const numberOfCourses = coursesList.length;
    let instructorPageMetaDescription;

    if (numberOfCourses && numberOfCourses > 1 && !!partnerName) {
      instructorPageMetaDescription = _t(
        '#{instructorName} is an instructor from #{partnerName}, teaching #{numberOfCourses} online courses on Coursera, such as #{firstCourseName} and #{secondCourseName}. ',
        {
          instructorName,
          partnerName,
          numberOfCourses,
          // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          firstCourseName: coursesList[0].name,
          // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          secondCourseName: coursesList[1].name,
        }
      );
    } else if (numberOfCourses === 1 && !!partnerName) {
      instructorPageMetaDescription = _t(
        '#{instructorName} is an instructor from #{partnerName}, teaching #{numberOfCourses} online course on Coursera: #{courseName}.',
        {
          instructorName,
          partnerName,
          numberOfCourses,
          // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          courseName: coursesList[0].name,
        }
      );
    } else if (numberOfCourses === 0 && !!partnerName) {
      instructorPageMetaDescription = _t('#{instructorName} is an instructor from #{partnerName}.', {
        instructorName,
        partnerName,
      });
    } else {
      instructorPageMetaDescription = _t('#{instructorName} is an instructor on Coursera.', {
        instructorName,
      });
    }

    return instructorPageMetaDescription;
  };

  const metatagTitle = _t('#{instructorName}, Instructor', { instructorName });
  const metaDescription =
    bio.replace(/<(?:.|\n)*?>/gm, '').replace(/&nbsp;/gm, '') || constructDescriptionMetaTagValue();
  const isActiveInstructor = coursesList.length >= 1;
  const { isInternationalSubdomain } = getSubdomainDetails(location);
  const breadcrumbLinks = getInstructorBreadcrumbLinks(instructor);
  const supportedHreflangs = [
    {
      rel: 'alternate',
      href: generateHref(location, 'www'),
      hreflang: 'x-default',
    },
  ];

  return (
    <div>
      <InstructorProfilePage instructor={instructor} />
      <CourseraMetatags
        metaNameAndProperties={{
          title: metatagTitle,
          description: metaDescription,
          disableCourseraDescriptionSuffix: true,
          useFollowForDisablingIndexing: true,
          disableCrawlerIndexing: !isActiveInstructor || isInternationalSubdomain,
        }}
        hrefLangOverrides={() => supportedHreflangs}
      />
      <InstructorProfileGoogleSchemaMarkup
        instructor={instructor}
        description={metaDescription}
        pathname={pathname}
        breadcrumbLinks={breadcrumbLinks}
      />
    </div>
  );
};

const InstructorProfileByShortName = gql`
  query InstructorProfileViewerByShortNameQuery($shortName: String!) {
    InstructorsV1Resource {
      shortName(shortName: $shortName) {
        instructors: elements {
          ...InstructorProfileFragment
        }
      }
    }
  }
  ${InstructorProfilePage.fragments.instructorProfile}
`;

const InstructorProfileById = gql`
  query InstructorProfileViewerByIdQuery($id: String!) {
    InstructorsV1Resource {
      get(id: $id) {
        ...InstructorProfileFragment
      }
    }
  }
  ${InstructorProfilePage.fragments.instructorProfile}
`;

export default compose<PropsToComponent, PropsFromRouter>(
  branch<PropsFromRouter>(
    // This component supports fetching instructor profile by both instructor shortName (i.e. 'andrewng') and instructor id (i.e. 1244).
    // Instructor ids are denoted by a slug starting with a tilde (~) and are integers (i.e ~1244)
    ({ params: { instructorSlug } }) => instructorSlug.includes('~') && !Number.isNaN(Number(instructorSlug.slice(1))),
    waitForGraphQL<PropsFromRouter, InstructorProfileViewerByIdQueryData, ByIdVariables, PropsToComponent>(
      InstructorProfileById,
      {
        props: ({ data }) => {
          let instructor;
          if (data) {
            instructor = data.InstructorsV1Resource && data.InstructorsV1Resource.get;
          }

          return {
            instructor,
          };
        },
        options({ params: { instructorSlug } }) {
          return {
            variables: {
              id: instructorSlug.slice(1),
            },
          };
        },
      }
    ),
    waitForGraphQL<
      PropsFromRouter,
      InstructorProfileViewerByShortNameQueryData,
      ByShortNameVariables,
      PropsToComponent
    >(InstructorProfileByShortName, {
      props: ({ data }) => {
        let instructors: Array<Instructor | null> = [];

        if (data) {
          instructors = data.InstructorsV1Resource?.shortName?.instructors || [];
        }

        return {
          instructor: instructors[0],
        };
      },
      options({ params: { instructorSlug } }) {
        return {
          variables: {
            shortName: instructorSlug,
          },
        };
      },
    })
  ),
  withNotFound({
    test: ({ instructor }) => !instructor || (instructor && isAffiliatedToBlockedRussianPartner(instructor)),
    errorComponent: InstructorProfileNotFound,
  })
)(InstructorProfileViewer);
